import React, { useRef, useState } from 'react'
import { ColumnProps } from 'antd/lib/table'
import { Form } from 'antd'
import { Formik, FormikProps } from 'formik'
import { ObjectSchema } from 'yup'
import { PaginationConfig } from 'antd/lib/pagination/Pagination'

import { Button, Drawer, PageHeader, Table, Text } from 'src/components/Ant'
import { ResourcesAvailable } from 'src/core/store/ducks/resources/types'
import useDocumentTitle from 'src/hooks/use-document-title'

import * as CSS from './styles'

type Resource = ResourcesAvailable[keyof ResourcesAvailable]

export interface DataColumnsProps {
  editCurrent: (item: Resource, sendUpdate: boolean) => void
}

export type GetDrawerType = <T>(formikProps: FormikProps<T>) => JSX.Element

interface PaginationProps {
  current: number
  pageSize: number
  total: number
}

export type ResourcesCrudDataColumnsType = (
  props: DataColumnsProps
) => ColumnProps<unknown>[]
export type ResourcesCrudDrawerFormType = (
  formikProps: FormikProps<Resource> //Resource - any
) => JSX.Element
export type ResourcesCrudOnSubmitReturn = (values: Resource) => Promise<void>

export interface ResourcesCrudProps {
  dataColumns: ResourcesCrudDataColumnsType
  dataSource: Resource[]
  drawerForm: ResourcesCrudDrawerFormType
  getPage: (page: number) => void
  header: JSX.Element
  initialValues: Resource
  isLoading: boolean
  onSubmit: ResourcesCrudOnSubmitReturn
  pagination: PaginationProps
  title: string
  validationSchema: () => ObjectSchema<Resource>
  width: string
}

export default function ResourcesCrud({
  dataColumns,
  dataSource,
  drawerForm,
  getPage,
  header,
  initialValues,
  isLoading,
  onSubmit,
  pagination,
  title,
  validationSchema,
  width
}: ResourcesCrudProps) {
  useDocumentTitle('web.resources.title-plural')

  const [current, setCurrent] = useState<Resource | null>(null)
  const isSubscribed = useRef<boolean>(true)
  const refFormik = useRef<Formik<any>>(undefined)

  function editCurrent(
    item: ResourcesAvailable[keyof ResourcesAvailable],
    sendUpdate: boolean = false
  ): void {
    if (isSubscribed.current) {
      if (sendUpdate) {
        onSubmit(item)
        setCurrent(null)
      } else {
        setCurrent(item as Resource)
      }
    }
  }

  const columns = dataColumns({
    editCurrent
  })

  function handlePageChange(page: PaginationConfig) {
    getPage(page.current)
  }

  const isNew = !current?.name
  const drawerTitle = (
    <Text
      onlyText
      title={isNew ? 'common.actions.add' : 'common.actions.change'}
    />
  )

  return (
    <>
      <CSS.Breadcrumbs>{header}</CSS.Breadcrumbs>
      <PageHeader
        extra={[
          <Button
            key="common.actions.add"
            onClick={() => {
              setCurrent(initialValues)
            }}
            size="default"
            title="common.actions.add"
            type="primary"
          />
        ]}
        title={title}
      />
      <CSS.Content>
        <Table
          className="table-striped"
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          onChange={handlePageChange}
          pagination={pagination}
        />
      </CSS.Content>
      <Drawer
        footer={
          <CSS.DrawerFooterDiv>
            <Button
              onClick={async () => {
                setCurrent(null)
              }}
              title="common.actions.cancel"
              type="default"
            />
            <CSS.Button
              onClick={async () => {
                if (refFormik.current) {
                  const hasErrors = await refFormik.current?.validateForm(
                    refFormik.current.getFormikBag().values
                  )
                  if (!Object.keys(hasErrors).length) {
                    refFormik.current.submitForm()
                    setCurrent(null)
                  }
                }
              }}
              title="common.actions.save"
              type="primary"
            />
          </CSS.DrawerFooterDiv>
        }
        onClose={async () => {
          setCurrent(null)
        }}
        title={drawerTitle}
        visible={Boolean(current)}
        width={width}
      >
        <Formik
          initialValues={current}
          onSubmit={onSubmit}
          ref={refFormik}
          render={formikProps => (
            <Form onSubmit={formikProps.handleSubmit}>
              {drawerForm(formikProps)}
            </Form>
          )}
          validationSchema={validationSchema}
        />
      </Drawer>
    </>
  )
}
